import { isKBRCatalog } from '@utils/catalog/kbrCatalog'
import { isZocaloZone } from '@utils/debug/compareLinkedItemMaterials'
import { Textures, Zone } from '@types'
import { Box3, Mesh, Object3D, Vector3 } from 'three'

type CatalogRef = {
  catalogID?: string | number | null
  codigo?: string | number | null
  referencia?: string | number | null
} | null | undefined

type MorphLike = {
  name?: string
  default?: number
  _default?: number
  min?: number
  max?: number
}

/** Alto fijo deseado del zócalo integrado KBR (mm). */
export const KBR_INTEGRATED_ZOCALO_HEIGHT_MM = 100

const BASE_POS_Y = '_integratedZocaloBasePosY'
const BASE_SCALE_Y = '_integratedZocaloBaseScaleY'
const NATIVE_HZ = '_integratedZocaloNativeHeight'
export const KBR_ALTO_COMP = '_kbrAltoComp'
export const KBR_ALTO_EPOCH = '_kbrAltoEpoch'
export const KBR_R3F_MESH_REF = '_kbrR3fMeshRef'
export const KBR_R3F_MESH_REFS = '_kbrR3fMeshRefs'
export const KBR_ALTO_VISUAL = '_kbrAltoVisual'

export type KbrAltoComp = {
  posY: number
  scaleY: number
  /** Influences a copiar al mesh R3F (mismo orden que morphTargetInfluences). */
  influences?: number[]
}

export function registerKbrR3fMesh(glbNode: Mesh, r3f: Mesh | null | undefined): void {
  let set = glbNode.userData[KBR_R3F_MESH_REFS] as Set<Mesh> | undefined
  if (!set) {
    set = new Set()
    glbNode.userData[KBR_R3F_MESH_REFS] = set
  }
  if (r3f?.isMesh) {
    set.add(r3f)
    glbNode.userData[KBR_R3F_MESH_REF] = r3f
  }
}

export function unregisterKbrR3fMesh(glbNode: Mesh, r3f: Mesh | null | undefined): void {
  const set = glbNode.userData[KBR_R3F_MESH_REFS] as Set<Mesh> | undefined
  if (set && r3f) set.delete(r3f)
  if (glbNode.userData[KBR_R3F_MESH_REF] === r3f) {
    glbNode.userData[KBR_R3F_MESH_REF] = set?.values().next().value ?? null
  }
}

const LOG_STYLE = 'color:#e67e22;font-weight:bold'

export function usesIntegratedZocaloAltoPolicy(
  item: CatalogRef & { hasIndependentZocalo?: boolean },
): boolean {
  if (!item) return false
  // También con tiras Zocalo 4000: el Alto extra (p.ej. 1100 > morph 900) hay que
  // aplicarlo en el cuerpo con pivote en la junta (100 mm), no con scaleY de grupo
  // desde Y=0 (eso levanta el hueco del zócalo y el panel queda flotando).
  return (
    isKBRCatalog(item.catalogID) ||
    isKBRCatalog(item.codigo) ||
    isKBRCatalog(item.referencia)
  )
}

const ZOCALO_LABEL = /z[oó]calo/i
const REGLETA_LABEL = /regleta|mesh004/i
const PATAS_LABEL = /patas?/i

export function isIntegratedZocaloMesh(
  mesh: Mesh,
  zones?: Zone,
  textures?: Textures,
): boolean {
  const zoneEntry = zones?.[mesh.name as keyof Zone]
  const tex = textures?.find((t) => t.zoneName === mesh.name)
  if (zoneEntry && isZocaloZone(zoneEntry, tex as never)) return true
  const labels = [
    mesh.name,
    zoneEntry?.name,
    tex?.zoneDisplayName,
    tex?.zoneName,
    (tex as { zone?: string } | undefined)?.zone,
    (mesh.material as { name?: string })?.name,
  ]
  return labels.some((label) => ZOCALO_LABEL.test(String(label ?? '')))
}

function isRegletaMesh(mesh: Mesh): boolean {
  return REGLETA_LABEL.test(String(mesh.name ?? ''))
}

function isPatasMesh(mesh: Mesh, zones?: Zone, textures?: Textures): boolean {
  const zoneEntry = zones?.[mesh.name as keyof Zone]
  const tex = textures?.find((t) => t.zoneName === mesh.name)
  const labels = [
    mesh.name,
    zoneEntry?.name,
    tex?.zoneDisplayName,
    tex?.zoneName,
    (mesh.material as { name?: string })?.name,
  ]
  return labels.some((label) => PATAS_LABEL.test(String(label ?? '')))
}

/** Mallas del cuerpo (puertas, costado Box002, carcasa). No zócalo L / patas / regleta. */
export function shouldReceiveKbrAltoMorph(
  mesh: Mesh,
  zones?: Zone,
  textures?: Textures,
): boolean {
  if (isIntegratedZocaloMesh(mesh, zones, textures)) return false
  if (isPatasMesh(mesh, zones, textures)) return false
  if (isRegletaMesh(mesh)) return false
  if (isKbrFixedHeightMeshName(String(mesh.name ?? ''))) return false
  return Boolean((mesh as Mesh).isMesh)
}

/**
 * Ya no congelamos Alto: el morph estira todo (sin hueco) y luego
 * comprimimos el zócalo a 100 mm pasando el exceso al cuerpo.
 */
export function shouldFreezeKbrAltoMorph(
  _mesh: Mesh,
  _zones?: Zone,
  _textures?: Textures,
): boolean {
  return false
}

/** @deprecated No usar en KBR con política de redistribución. */
export function freezeIntegratedZocaloAltoMorph(node: {
  morphTargetDictionary?: Record<string, number>
  morphTargetInfluences?: number[]
  name?: string
}): void {
  const dict = node.morphTargetDictionary
  const influences = node.morphTargetInfluences
  if (!dict || !influences) return
  const idx = dict['Alto']
  if (idx == null) return
  influences[idx] = 0
}

export function kbrAltoAllowsBeyondMorphMax(item: CatalogRef): boolean {
  return usesIntegratedZocaloAltoPolicy(item)
}

export function resetIntegratedZocaloAltoUserData(root: Object3D): void {
  root.userData[NATIVE_HZ] = undefined
  root.userData[KBR_ALTO_EPOCH] = 0
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    delete mesh.userData[BASE_POS_Y]
    delete mesh.userData[BASE_SCALE_Y]
    delete mesh.userData[KBR_ALTO_VISUAL]
    delete mesh.userData[KBR_ALTO_COMP]
    delete mesh.userData[KBR_R3F_MESH_REF]
    delete mesh.userData[KBR_R3F_MESH_REFS]
  })
}

function getAltoInfluence(mesh: Mesh): number | null {
  const idx = mesh.morphTargetDictionary?.['Alto']
  if (idx == null || !mesh.morphTargetInfluences) return null
  return mesh.morphTargetInfluences[idx] ?? null
}

function setAltoInfluence(mesh: Mesh, value: number): void {
  const idx = mesh.morphTargetDictionary?.['Alto']
  if (idx == null || !mesh.morphTargetInfluences) return
  mesh.morphTargetInfluences[idx] = Math.max(0, Math.min(1, value))
}

function influenceFromMorph(alto?: MorphLike | null): number {
  if (!alto) return 0
  const min = Number(alto.min)
  const max = Number(alto.max)
  const value = Number(alto.default)
  if (!(max > min) || !Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, (value - min) / (max - min)))
}

function getMorphedLocalBox(mesh: Mesh, target: Object3D): Box3 {
  const geo = mesh.geometry
  const pos = geo?.attributes?.position
  const box = new Box3()
  if (!pos) {
    box.setFromObject(mesh)
    return box
  }

  mesh.updateWorldMatrix(true, false)
  target.updateWorldMatrix(true, false)
  const inv = target.matrixWorld.clone().invert()
  const v = new Vector3()
  const morphAttrs = geo.morphAttributes?.position
  const influences = mesh.morphTargetInfluences

  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i)
    if (morphAttrs && influences) {
      for (let m = 0; m < morphAttrs.length; m++) {
        const inf = influences[m] || 0
        if (!inf || !morphAttrs[m]) continue
        v.x += morphAttrs[m].getX(i) * inf
        v.y += morphAttrs[m].getY(i) * inf
        v.z += morphAttrs[m].getZ(i) * inf
      }
    }
    v.applyMatrix4(mesh.matrixWorld).applyMatrix4(inv)
    box.expandByPoint(v)
  }
  return box
}

function unionMorphedBoxes(
  meshes: Mesh[],
  target: Object3D,
  opts?: { includeHidden?: boolean },
): Box3 | null {
  const box = new Box3()
  let has = false
  for (const mesh of meshes) {
    // KBR: el zócalo integrado puede estar hidden (sustituido por Zocalo 4000)
    // pero sigue haciendo falta para medir la unión / comprimir a 100 mm.
    if (!opts?.includeHidden && !mesh.visible) continue
    const b = getMorphedLocalBox(mesh, target)
    if (b.isEmpty()) continue
    if (!has) {
      box.copy(b)
      has = true
    } else {
      box.union(b)
    }
  }
  return has ? box : null
}

function collectMeshes(root: Object3D): Mesh[] {
  const meshes: Mesh[] = []
  root.traverse((obj) => {
    if ((obj as Mesh).isMesh) meshes.push(obj as Mesh)
  })
  return meshes
}

function ensureBaseTransforms(mesh: Mesh): void {
  if (typeof mesh.userData[BASE_POS_Y] !== 'number') {
    mesh.userData[BASE_POS_Y] = mesh.position.y
  }
  if (typeof mesh.userData[BASE_SCALE_Y] !== 'number') {
    const sy = mesh.scale.y
    mesh.userData[BASE_SCALE_Y] = Number.isFinite(sy) && sy > 0 ? sy : 1
  }
}

function resetMeshY(mesh: Mesh): void {
  ensureBaseTransforms(mesh)
  mesh.position.y = mesh.userData[BASE_POS_Y] as number
  mesh.scale.y = mesh.userData[BASE_SCALE_Y] as number
}

function readAltoMm(morphs?: MorphLike[] | null, fallbackHeight?: number): number {
  const alto = morphs?.find((m) => m.name === 'Alto')
  const fromMorph = Number(alto?.default ?? 0)
  if (fromMorph > 0) return fromMorph
  const h = Number(fallbackHeight ?? 0)
  return h > 0 ? h : 0
}

function isKbrFixedHeightMeshName(name: string): boolean {
  return /^Mesh\d+_(?!1$)\d+$/i.test(name)
}

/** Mesh integrado a ocultar cuando KBR usa tira Zocalo 4000 (Mesh001_2 / zona zócalo). */
export function shouldHideKbrIntegratedZocaloMesh(
  mesh: Mesh,
  zones?: Zone,
  textures?: Textures,
): boolean {
  return (
    isIntegratedZocaloMesh(mesh, zones, textures) ||
    isKbrFixedHeightMeshName(String(mesh.name ?? ''))
  )
}

/**
 * Tras ocultar el zócalo L, el cuerpo del KBR sigue modelado a Y≈100 mm (encima de
 * esa losa). El grupo 3D ya aplica lift 100 para la tira Zocalo 4000: si no se
 * baja el mesh a Y=0, el mueble flota y se ve más bajo que el catálogo.
 * Solo actúa si el hueco residual parece zócalo (40–160 mm).
 */
export function snapKbrBodyToFloorAfterHidingIntegratedZocalo(
  root: Object3D | null | undefined,
): boolean {
  if (!root) return false
  root.updateMatrixWorld(true)
  const box = new Box3()
  let hasMesh = false
  root.traverse((obj) => {
    const mesh = obj as Mesh
    if (!mesh.isMesh || mesh.visible === false) return
    hasMesh = true
    box.expandByObject(mesh)
  })
  if (!hasMesh || box.isEmpty()) return false
  const delta = box.min.y
  if (!(delta >= 0.04 && delta <= 0.16)) return false
  for (const child of root.children) {
    child.position.y -= delta
  }
  root.updateMatrixWorld(true)
  return true
}

/** Escala Y con pivote en un Y fijo (espacio local del root). */
function scaleMeshesYAboutPivot(
  meshes: Mesh[],
  root: Object3D,
  scaleFactor: number,
  pivotY: number,
): void {
  if (Math.abs(scaleFactor - 1) < 0.002) return
  for (const mesh of meshes) {
    const py = mesh.position.y
    const baseScaleY = mesh.userData[BASE_SCALE_Y] as number
    mesh.scale.y = baseScaleY * scaleFactor
    mesh.position.y = pivotY + (py - pivotY) * scaleFactor
  }
  root.updateMatrixWorld(true)
}

function snapMeshesMinY(meshes: Mesh[], root: Object3D, targetMinY: number): void {
  const box = unionMorphedBoxes(meshes, root)
  if (!box || box.isEmpty()) return
  const delta = box.min.y - targetMinY
  if (!Number.isFinite(delta) || Math.abs(delta) < 1e-7) return
  for (const mesh of meshes) {
    mesh.position.y -= delta
  }
  root.updateMatrixWorld(true)
}

export function syncKbrAltoVisualToR3f(source: Mesh, r3f: Mesh | null | undefined): void {
  if (!r3f || !r3f.isMesh) return

  const comp = source.userData[KBR_ALTO_COMP] as KbrAltoComp | undefined
  const posY = comp?.posY ?? source.position.y
  const scaleY = comp?.scaleY ?? source.scale.y

  if (Number.isFinite(posY)) r3f.position.y = posY
  if (Number.isFinite(scaleY) && scaleY > 0.05 && scaleY < 20) r3f.scale.y = scaleY

  if (!r3f.morphTargetInfluences) return

  const live = source.morphTargetInfluences
  const stamped = comp?.influences
  const altoIdx = source.morphTargetDictionary?.['Alto']
  const n = r3f.morphTargetInfluences.length
  for (let i = 0; i < n; i++) {
    // Alto: snapshot de la policy (handleNodeMorphs lo pisa con el sidebar).
    // Resto (Regleta, Ancho, Fondo): valor vivo tras handleNodeMorphs.
    // Si se copian todas las influences del stamp, cambiar Regleta no mueve el GLB
    // padre (zócalo/encimera sí, porque no pasan por este sync).
    if (i === altoIdx && stamped && i < stamped.length) {
      r3f.morphTargetInfluences[i] = stamped[i]
    } else if (live && i < live.length) {
      r3f.morphTargetInfluences[i] = live[i]
    } else if (stamped && i < stamped.length) {
      r3f.morphTargetInfluences[i] = stamped[i]
    }
  }
}

function stampVisual(source: Mesh): void {
  const influences = source.morphTargetInfluences
    ? Array.from(source.morphTargetInfluences)
    : undefined
  const comp: KbrAltoComp = {
    posY: source.position.y,
    scaleY: source.scale.y,
    influences,
  }
  source.userData[KBR_ALTO_COMP] = comp
  source.userData[KBR_ALTO_VISUAL] = {
    posY: comp.posY,
    scaleY: comp.scaleY,
    altoInfluence: getAltoInfluence(source),
  }
  const set = source.userData[KBR_R3F_MESH_REFS] as Set<Mesh> | undefined
  if (set && set.size > 0) {
    for (const r3f of set) syncKbrAltoVisualToR3f(source, r3f)
  } else {
    syncKbrAltoVisualToR3f(source, source.userData[KBR_R3F_MESH_REF] as Mesh | null)
  }
}

/**
 * KBR Alto — redistribuir crecimiento del zócalo al cuerpo:
 *
 * 1) Morph Alto igual en cuerpo + zócalo → unidos (sin hueco).
 * 2) Comprimir zócalo a 100 mm.
 * 3) Cuerpo = Alto − 100 mm, pegado al zócalo (solo el cuerpo pierde/ajusta esos 100).
 */
export function applyIntegratedZocaloAltoPolicy(
  root: Object3D,
  opts: {
    morphs?: MorphLike[] | null
    heightMm?: number
    zones?: Zone
    textures?: Textures
    mmToScene?: number
    catalogID?: string | number
    zocaloHeightMm?: number
    /** Store = mueble completo (API 900/1100 incluye hueco de zócalo). */
    independentZocalo?: boolean
  },
): boolean {
  if (!root) return false

  const altoMorph = opts.morphs?.find((m) => m.name === 'Alto')
  const allMeshes = collectMeshes(root)
  const bodyMeshes = allMeshes.filter((m) =>
    shouldReceiveKbrAltoMorph(m, opts.zones, opts.textures),
  )
  const zocaloMeshes = allMeshes.filter(
    (m) =>
      isIntegratedZocaloMesh(m, opts.zones, opts.textures) ||
      isKbrFixedHeightMeshName(String(m.name ?? '')),
  )

  if (bodyMeshes.length === 0 || (!opts.independentZocalo && zocaloMeshes.length === 0)) {
    console.warn('%c[KBR Alto] abort.classify', LOG_STYLE, {
      body: bodyMeshes.map((m) => m.name),
      zocalo: zocaloMeshes.map((m) => m.name),
    })
    return false
  }

  const mmToScene = opts.mmToScene ?? 0.001
  const targetZocaloMm = opts.zocaloHeightMm ?? KBR_INTEGRATED_ZOCALO_HEIGHT_MM
  const targetZocaloH = targetZocaloMm * mmToScene
  const targetAltoMm = readAltoMm(opts.morphs, opts.heightMm)
  // Sidebar/store KBR = mueble (como el API): 1100 → cuerpo 1000 + junta 100.
  // No usar morph.default clampado a 900: si no el extra se pierde o se escala desde Y=0.
  const furnitureMm =
    Number(opts.heightMm) > 0 ? Number(opts.heightMm) : targetAltoMm

  const morphInfluence = influenceFromMorph(altoMorph)
  const meshInfluence = Math.max(0, ...bodyMeshes.map((m) => getAltoInfluence(m) ?? 0))
  const altoInfluence = opts.independentZocalo
    ? morphInfluence
    : meshInfluence > 1e-6
      ? meshInfluence
      : morphInfluence

  // Reset compensación previa
  for (const mesh of [...bodyMeshes, ...zocaloMeshes]) {
    resetMeshY(mesh)
  }

  // 1) Morph Alto igual en todos → unidos (como el GLB)
  for (const mesh of [...bodyMeshes, ...zocaloMeshes]) {
    setAltoInfluence(mesh, altoInfluence)
  }
  root.updateMatrixWorld(true)

  if (opts.independentZocalo) {
    // Morph hasta el max API. El extra (1100 vs 900) lo escala LinkedItem3D
    // con pivote en Y=100 mm; scale de malla desde el origen levanta el hueco.
    for (const mesh of [...bodyMeshes, ...zocaloMeshes]) stampVisual(mesh)
    root.userData[NATIVE_HZ] = targetZocaloH
    root.userData[KBR_ALTO_EPOCH] = (Number(root.userData[KBR_ALTO_EPOCH]) || 0) + 1
    return true
  }

  const zBox = unionMorphedBoxes(zocaloMeshes, root, { includeHidden: true })
  const bBox = unionMorphedBoxes(bodyMeshes, root)
  if (!zBox || zBox.isEmpty() || !bBox || bBox.isEmpty()) {
    console.warn('%c[KBR Alto] abort.boxEmpty', LOG_STYLE, {})
    return false
  }

  const zMin = zBox.min.y
  const zGrownH = zBox.max.y - zBox.min.y
  const excess = Math.max(0, zGrownH - targetZocaloH)
  root.userData[NATIVE_HZ] = targetZocaloH

  // 2) Zócalo → exactamente 100 mm, pivote en el suelo (zMin)
  let zScale = 1
  if (zGrownH > 1e-8) {
    zScale = targetZocaloH / zGrownH
    if (!Number.isFinite(zScale) || zScale <= 0.05 || zScale > 20) {
      console.warn('[KBR Alto] zScale fuera de rango', { zScale, zGrownH, targetZocaloH })
      zScale = 1
    } else {
      scaleMeshesYAboutPivot(zocaloMeshes, root, zScale, zMin)
      // Asegurar suelo del zócalo
      snapMeshesMinY(zocaloMeshes, root, zMin)
    }
  }

  const zAfter = unionMorphedBoxes(zocaloMeshes, root, { includeHidden: true })
  const junction = zAfter?.max.y ?? zMin + targetZocaloH

  // 3) Cuerpo = Alto − 100 mm (solo al cuerpo; el zócalo ya es 100).
  //    Pegar al zócalo y escalar para esa altura exacta.
  const desiredBodyMm =
    targetAltoMm > targetZocaloMm ? targetAltoMm - targetZocaloMm : targetZocaloMm * 0.01
  const desiredBodyH = desiredBodyMm * mmToScene

  snapMeshesMinY(bodyMeshes, root, junction)

  let bodyScale = 1
  const bodyAfterSnap = unionMorphedBoxes(bodyMeshes, root)
  if (bodyAfterSnap && !bodyAfterSnap.isEmpty()) {
    const heightAbove = Math.max(bodyAfterSnap.max.y - junction, 1e-8)
    bodyScale = desiredBodyH / heightAbove

    if (!Number.isFinite(bodyScale) || bodyScale <= 0.05 || bodyScale > 20) {
      console.warn('[KBR Alto] bodyScale fuera de rango', {
        bodyScale,
        heightAbove,
        desiredBodyMm,
      })
      bodyScale = 1
    }

    if (Math.abs(bodyScale - 1) >= 0.002) {
      scaleMeshesYAboutPivot(bodyMeshes, root, bodyScale, junction)
      snapMeshesMinY(bodyMeshes, root, junction)
    }
  }

  for (const mesh of [...bodyMeshes, ...zocaloMeshes]) stampVisual(mesh)
  root.userData[KBR_ALTO_EPOCH] = (Number(root.userData[KBR_ALTO_EPOCH]) || 0) + 1

  return true
}
